/**
 * Import Bank Statements Wizard Screen
 * Full-screen 3-step wizard: Upload -> Preview/Mapping -> Done
 * Extracted from CsvImportModal for better UX as a standalone screen.
 * Returns to Chat on completion (like FinancialDiagnosticWizard).
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { formatCurrency } from '../utils/formatters';
import { budgetApi } from '../api/endpoints/budget';
import { useBudgetStore } from '../store/budgetStore';
import { demoApi, type DemoProfile } from '../api/endpoints/demo';
import { useDemoMode } from '../contexts/DemoModeContext';
import type {
  CsvColumnMapping,
  CsvPreviewRow,
  CsvImportStats,
  CsvImportError,
  AccountDetectionResponse,
  AccountMatch,
  ExistingAccount,
  CreateAccountParams,
  PdfImportPreviewResponse,
} from '../api/types';
import logger from '../utils/logger';
import { openWhatsAppSupport } from '../utils/whatsapp';

function showAlert(title: string, message: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.alert(`${title}: ${message}`);
  } else {
    Alert.alert(title, message, [
      { text: 'OK', style: 'cancel' },
      { text: 'Contacter le support', onPress: () => openWhatsAppSupport(`Import: ${message}`) },
    ]);
  }
}

type Step = 'upload' | 'account' | 'preview';

const FIELD_LABELS: Record<string, string> = {
  date: 'Date',
  description: 'Description',
  amount: 'Montant',
  credit: 'Crédit',
  debit: 'Débit',
};

export default function ImportBankStatementsWizardScreen() {
  const navigation = useNavigation();
  const { refreshDemoStatus } = useDemoMode();

  const [step, setStep] = useState<Step>('upload');
  const [isProcessing, setIsProcessing] = useState(false);
  const [rawCsvContent, setRawCsvContent] = useState('');
  const [filename, setFilename] = useState<string | undefined>();
  const [fileFormat, setFileFormat] = useState<'csv' | 'pdf'>('csv');

  // Account detection state
  const [accountDetection, setAccountDetection] = useState<AccountDetectionResponse | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [createNewAccount, setCreateNewAccount] = useState(false);

  // PDF-specific state
  const [bankDetected, setBankDetected] = useState<string | null>(null);
  const [parserUsed, setParserUsed] = useState<string | null>(null);
  const [availableParsers, setAvailableParsers] = useState<{ key: string; label: string }[]>([]);
  const [selectedBankHint, setSelectedBankHint] = useState<string | null>(null);

  // Demo mode state
  const [demoProfiles, setDemoProfiles] = useState<DemoProfile[]>([]);
  const [isLoadingDemo, setIsLoadingDemo] = useState(false);
  const [activatingProfileKey, setActivatingProfileKey] = useState<string | null>(null);

  // Backend response data
  const [columnMapping, setColumnMapping] = useState<CsvColumnMapping | null>(null);
  const [mappingComplete, setMappingComplete] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<CsvPreviewRow[]>([]);
  const [stats, setStats] = useState<CsvImportStats | null>(null);
  const [errors, setErrors] = useState<CsvImportError[]>([]);

  // UI state
  const [showMappingEditor, setShowMappingEditor] = useState(false);
  const [showMappingDropdown, setShowMappingDropdown] = useState<string | null>(null);

  // Load demo profiles on mount
  useEffect(() => {
    const loadDemoProfiles = async () => {
      try {
        setIsLoadingDemo(true);
        const profiles = await demoApi.getProfiles();
        setDemoProfiles(profiles);
      } catch (err) {
        logger.error('[ImportWizard] Failed to load demo profiles:', err);
        // Silent fail - demo section just won't show
      } finally {
        setIsLoadingDemo(false);
      }
    };
    loadDemoProfiles();
  }, []);

  // Activate demo profile
  const handleActivateDemo = async (profileKey: string) => {
    try {
      setActivatingProfileKey(profileKey);
      const result = await demoApi.activate(profileKey);
      logger.info('[ImportWizard] Demo activated:', result.profile.name);

      // Refresh demo mode context so banner shows everywhere
      await refreshDemoStatus();

      // Navigate to chat to see the demo data in action
      (navigation as any).navigate('MainContent', {
        initialRoute: 'Chats',
        demoActivated: true,
        demoProfileName: result.profile.name,
      });
    } catch (err: any) {
      logger.error('[ImportWizard] Demo activation error:', err);
      const message = err.response?.data?.error || 'Erreur lors de l\'activation du mode demo';
      showAlert('Erreur', message);
    } finally {
      setActivatingProfileKey(null);
    }
  };

  const resetState = () => {
    setStep('upload');
    setIsProcessing(false);
    setRawCsvContent('');
    setFilename(undefined);
    setFileFormat('csv');
    setAccountDetection(null);
    setSelectedAccountId(null);
    setCreateNewAccount(false);
    setColumnMapping(null);
    setMappingComplete(false);
    setCsvHeaders([]);
    setPreviewRows([]);
    setStats(null);
    setErrors([]);
    setShowMappingEditor(false);
    setShowMappingDropdown(null);
    setBankDetected(null);
    setParserUsed(null);
    setAvailableParsers([]);
    setSelectedBankHint(null);
  };

  const handleClose = () => {
    resetState();
    navigation.goBack();
  };

  // ── File picking ────────────────────────────────────────────────────────

  const isPdfFile = (fname: string) => fname.toLowerCase().endsWith('.pdf');

  const handleFilePickNative = async () => {
    try {
      const DocumentPicker = require('expo-document-picker');
      const result = await DocumentPicker.getDocumentAsync({
        type: ['text/csv', 'text/plain', 'application/vnd.ms-excel', 'application/pdf'],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const FileSystem = require('expo-file-system');
      const fname = result.assets[0].name;

      if (isPdfFile(fname)) {
        // PDF: read as base64
        const base64Content = await FileSystem.readAsStringAsync(result.assets[0].uri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        setFileFormat('pdf');
        detectAccountFromFile(base64Content, fname, 'pdf');
      } else {
        // CSV: read as text
        const content = await FileSystem.readAsStringAsync(result.assets[0].uri);
        setFileFormat('csv');
        detectAccountFromFile(content, fname, 'csv');
      }
    } catch (err: any) {
      logger.error('[ImportWizard] Native file pick error:', err);
      showAlert('Erreur', 'Impossible de lire le fichier');
    }
  };

  const handleFilePickWeb = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.txt,.pdf';
    input.style.display = 'none';
    document.body.appendChild(input);
    input.onchange = (e: any) => {
      const file = e.target?.files?.[0];
      document.body.removeChild(input);
      if (!file) return;

      if (isPdfFile(file.name)) {
        // PDF: read as base64
        const reader = new FileReader();
        reader.onload = (ev) => {
          const arrayBuffer = ev.target?.result as ArrayBuffer;
          if (arrayBuffer) {
            const bytes = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const base64Content = btoa(binary);
            setFileFormat('pdf');
            detectAccountFromFile(base64Content, file.name, 'pdf');
          } else {
            showAlert('Erreur', 'Le fichier semble vide');
          }
        };
        reader.onerror = () => showAlert('Erreur', 'Impossible de lire le fichier');
        reader.readAsArrayBuffer(file);
      } else {
        // CSV: read as text
        const reader = new FileReader();
        reader.onload = (ev) => {
          const content = ev.target?.result as string;
          if (content) {
            setFileFormat('csv');
            detectAccountFromFile(content, file.name, 'csv');
          } else {
            showAlert('Erreur', 'Le fichier semble vide');
          }
        };
        reader.onerror = () => showAlert('Erreur', 'Impossible de lire le fichier');
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleFilePick = Platform.OS === 'web' ? handleFilePickWeb : handleFilePickNative;

  // ── Backend calls ───────────────────────────────────────────────────────

  const detectAccountFromFile = async (content: string, fname?: string, format: 'csv' | 'pdf' = 'csv') => {
    setRawCsvContent(content);
    setFilename(fname);
    setIsProcessing(true);
    try {
      const detection = await budgetApi.detectAccount(content, fname, format);
      setAccountDetection(detection);

      // Auto-select if exact match with high confidence
      if (detection.suggestion === 'attach_existing' && detection.matches.length === 1) {
        setSelectedAccountId(detection.matches[0].account_id);
      } else if (detection.suggestion === 'create_new') {
        setCreateNewAccount(true);
      }

      setStep('account');
    } catch (err: any) {
      logger.error('[ImportWizard] Account detection error:', err);
      // Fall back to direct preview if detection fails
      sendToBackend(content);
    } finally {
      setIsProcessing(false);
    }
  };

  const sendToBackend = async (content: string, mapping?: CsvColumnMapping, bankHint?: string) => {
    setRawCsvContent(content);
    setIsProcessing(true);
    try {
      if (fileFormat === 'pdf') {
        const response = await budgetApi.importPdf(content, undefined, bankHint || undefined);
        setBankDetected(response.bank_detected);
        setParserUsed(response.parser_used);
        setAvailableParsers(response.available_parsers || []);
        setPreviewRows(response.rows || []);
        setStats(response.stats || null);
        setErrors(response.errors || []);
        setMappingComplete(true); // PDF has no column mapping
        setStep('preview');
      } else {
        const response = await budgetApi.importCsv(content, mapping);
        setColumnMapping(response.detected_mapping || null);
        setMappingComplete(response.mapping_complete ?? false);
        setCsvHeaders(response.headers || []);
        setPreviewRows(response.rows || []);
        setStats(response.stats || null);
        setErrors(response.errors || []);

        if (!response.mapping_complete) {
          setShowMappingEditor(true);
        }
        setStep('preview');
      }
    } catch (err: any) {
      logger.error('[ImportWizard] Backend error:', err);
      const message = err.response?.data?.error || "Erreur lors de l'analyse du fichier";
      showAlert('Erreur', message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResubmitMapping = () => {
    if (columnMapping && rawCsvContent) {
      setShowMappingEditor(false);
      sendToBackend(rawCsvContent, columnMapping);
    }
  };

  const proceedToPreview = () => {
    if (rawCsvContent) {
      sendToBackend(rawCsvContent);
    }
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccountId(accountId);
    setCreateNewAccount(false);
  };

  const handleCreateNewAccount = () => {
    setSelectedAccountId(null);
    setCreateNewAccount(true);
  };

  const handleConfirmImport = async () => {
    if (previewRows.length === 0) return;
    setIsProcessing(true);
    try {
      // Pass selected account ID if user chose an existing account
      const accountId = selectedAccountId || undefined;

      // If creating new account, pass detected info
      let createAccountParams: CreateAccountParams | undefined;
      if (createNewAccount && accountDetection?.detected) {
        const detected = accountDetection.detected;
        createAccountParams = {
          bank_name: detected.bank_name || undefined,
          account_type: detected.account_type || 'checking',
          account_number_masked: detected.account_number_masked || undefined,
          iban: detected.iban || undefined,
          balance: detected.balance ?? undefined,
        };
      }

      await budgetApi.confirmImport(previewRows, accountId, createAccountParams);

      // Navigate to Budget screen at the month of the imported transactions
      const dates = previewRows
        .map((r) => r.transaction_date)
        .filter(Boolean)
        .sort();
      if (dates.length > 0) {
        // Use the most recent transaction's month (YYYY-MM)
        const lastDate = dates[dates.length - 1];
        const importMonth = lastDate.substring(0, 7); // "2026-01-15" → "2026-01"
        useBudgetStore.getState().setMonth(importMonth);
      }

      (navigation as any).navigate('MainContent', {
        initialRoute: 'Chats',
        importCompleteTimestamp: Date.now(),
      });
    } catch (err: any) {
      logger.error('[ImportWizard] Confirm error:', err);
      const message = err.response?.data?.error || "Erreur lors de l'import";
      showAlert('Erreur', message);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateMapping = (field: keyof CsvColumnMapping, value: string) => {
    setColumnMapping((prev) => {
      if (!prev) return { date: '', description: '', [field]: value };
      return { ...prev, [field]: value };
    });
    setShowMappingDropdown(null);
  };

  // ── Derived state ───────────────────────────────────────────────────────

  const activeMappingEntries = columnMapping
    ? Object.entries(columnMapping).filter(([_, v]) => v)
    : [];

  const isMappingComplete = columnMapping
    ? !!(columnMapping.date && columnMapping.description && (columnMapping.amount || (columnMapping.credit && columnMapping.debit)))
    : false;

  // ── Progress indicator ────────────────────────────────────────────────

  const stepIndex = step === 'upload' ? 0 : step === 'account' ? 1 : 2;
  const progressPercent = ((stepIndex + 1) / 3) * 100;

  // ── Render helpers ──────────────────────────────────────────────────────

  const renderMappingSummary = () => (
    <View style={styles.mappingSummary}>
      <View style={styles.mappingSummaryHeader}>
        <View style={styles.mappingSummaryTitleRow}>
          <Icon name="check-circle" size={18} color={colors.success} />
          <Text style={styles.mappingSummaryTitle}>Colonnes détectées automatiquement</Text>
        </View>
        <TouchableOpacity onPress={() => setShowMappingEditor(!showMappingEditor)}>
          <Text style={styles.mappingEditLink}>Modifier</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.mappingChips}>
        {activeMappingEntries.map(([field, header]) => (
          <View key={field} style={styles.mappingChip}>
            <Text style={styles.mappingChipLabel}>{FIELD_LABELS[field] || field}</Text>
            <Icon name="arrow-right" size={12} color={colors.textSecondary} />
            <Text style={styles.mappingChipValue}>{header}</Text>
          </View>
        ))}
      </View>
    </View>
  );

  const renderMappingField = (field: keyof CsvColumnMapping, label: string, required: boolean) => {
    const currentValue = columnMapping?.[field] || '';
    const isOpen = showMappingDropdown === field;
    return (
      <View style={styles.mappingField} key={field}>
        <Text style={styles.mappingLabel}>
          {label} {required && <Text style={styles.requiredStar}>*</Text>}
        </Text>
        <TouchableOpacity
          style={styles.mappingSelect}
          onPress={() => setShowMappingDropdown(isOpen ? null : field)}
        >
          <Text style={[styles.mappingSelectText, !currentValue && styles.mappingPlaceholder]}>
            {currentValue || 'Sélectionner...'}
          </Text>
          <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={18} color={colors.textSecondary} />
        </TouchableOpacity>
        {isOpen && (
          <ScrollView style={styles.mappingDropdown} nestedScrollEnabled>
            <TouchableOpacity style={styles.mappingOption} onPress={() => updateMapping(field, '')}>
              <Text style={[styles.mappingOptionText, styles.mappingPlaceholder]}>-- Aucun --</Text>
            </TouchableOpacity>
            {csvHeaders.filter(h => h).map((header) => (
              <TouchableOpacity
                key={header}
                style={[styles.mappingOption, currentValue === header && styles.mappingOptionSelected]}
                onPress={() => updateMapping(field, header)}
              >
                <Text style={[styles.mappingOptionText, currentValue === header && styles.mappingOptionTextSelected]}>
                  {header}
                </Text>
                {currentValue === header && <Icon name="check" size={16} color={colors.primary} />}
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}
      </View>
    );
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Text style={styles.closeButtonText}>{'✕'}</Text>
          </TouchableOpacity>

          <Text style={styles.headerTitle}>Importer un relevé bancaire</Text>

          {/* Progress bar */}
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { width: `${progressPercent}%` }]} />
            </View>
            <View style={styles.stepLabels}>
              <Text style={[styles.stepLabel, step === 'upload' && styles.stepLabelActive]}>Fichier</Text>
              <Text style={[styles.stepLabel, step === 'account' && styles.stepLabelActive]}>Compte</Text>
              <Text style={[styles.stepLabel, step === 'preview' && styles.stepLabelActive]}>Analyse</Text>
            </View>
          </View>
        </View>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <View style={styles.content}>
            <View style={styles.uploadArea}>
              <Icon name="file-upload-outline" size={48} color={colors.primary} />
              <Text style={styles.uploadTitle}>Relevé bancaire</Text>
              <Text style={styles.uploadDescription}>
                Sélectionne un fichier CSV ou PDF exporté depuis ta banque.{'\n'}
                Les transactions seront détectées automatiquement.
              </Text>

              {Platform.OS === 'web' ? (
                <div
                  onClick={isProcessing ? undefined : handleFilePick}
                  style={{
                    display: 'flex',
                    flexDirection: 'row' as const,
                    alignItems: 'center',
                    gap: 8,
                    backgroundColor: isProcessing ? 'rgba(0,122,255,0.5)' : '#007AFF',
                    borderRadius: 8,
                    paddingTop: 12,
                    paddingBottom: 12,
                    paddingLeft: 32,
                    paddingRight: 32,
                    cursor: isProcessing ? 'default' : 'pointer',
                  }}
                >
                  {isProcessing ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <>
                      <Icon name="folder-open-outline" size={20} color="#FFFFFF" />
                      <Text style={styles.uploadButtonText}>Choisir un fichier</Text>
                    </>
                  )}
                </div>
              ) : (
                <TouchableOpacity style={styles.uploadButton} onPress={handleFilePick} disabled={isProcessing}>
                  {isProcessing ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <>
                      <Icon name="folder-open-outline" size={20} color="#FFFFFF" />
                      <Text style={styles.uploadButtonText}>Choisir un fichier</Text>
                    </>
                  )}
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.supportedFormats}>
              <Text style={styles.supportedTitle}>Formats supportés</Text>
              <Text style={styles.supportedText}>CSV : séparateurs auto-détectés (virgule, point-virgule, tabulation)</Text>
              <Text style={styles.supportedText}>PDF : relevés natifs des principales banques françaises</Text>
              <Text style={styles.supportedText}>Dates : JJ/MM/AAAA, AAAA-MM-JJ, JJ-MM-AAAA, JJ.MM.AAAA</Text>
            </View>

            <View style={styles.helpBox}>
              <Text style={styles.helpIcon}>{'💡'}</Text>
              <Text style={styles.helpText}>
                Tu peux exporter un relevé CSV depuis l'application de ta banque (rubrique "Télécharger" ou "Exporter").
              </Text>
            </View>

            {/* Value proposition */}
            <View style={styles.valueBox}>
              <Text style={styles.valueIcon}>{'💰'}</Text>
              <View style={styles.valueContent}>
                <Text style={styles.valueTitle}>Un bilan financier personnalisé</Text>
                <Text style={styles.valueText}>
                  Avec un conseiller, ça coûte en moyenne <Text style={styles.valueBold}>250€</Text>.{'\n'}
                  Avec Wealfi : <Text style={styles.valueBold}>9,99€/mois</Text> — soit 2 ans d'accompagnement pour le même prix.
                </Text>
              </View>
            </View>

            {/* Demo profiles section */}
            {demoProfiles.length > 0 && (
              <View style={styles.demoSection}>
                <View style={styles.demoDivider}>
                  <View style={styles.demoDividerLine} />
                  <Text style={styles.demoDividerText}>ou</Text>
                  <View style={styles.demoDividerLine} />
                </View>

                <Text style={styles.demoTitle}>Pas encore pret ? Essaie un profil fictif</Text>
                <Text style={styles.demoSubtitle}>
                  Decouvre ce que Wealfi peut detecter sans importer tes propres donnees
                </Text>

                {demoProfiles.map((profile) => (
                  <TouchableOpacity
                    key={profile.key}
                    style={styles.demoProfileCard}
                    onPress={() => handleActivateDemo(profile.key)}
                    disabled={activatingProfileKey !== null}
                  >
                    <View style={styles.demoProfileHeader}>
                      <Text style={styles.demoProfileEmoji}>
                        {String.fromCodePoint(parseInt(profile.emoji, 16))}
                      </Text>
                      <View style={styles.demoProfileInfo}>
                        <Text style={styles.demoProfileName}>{profile.name}</Text>
                        <Text style={styles.demoProfileTagline}>{profile.tagline}</Text>
                      </View>
                    </View>
                    <View style={styles.demoProfileFooter}>
                      <View style={styles.demoProfileSavings}>
                        <Text style={styles.demoProfileSavingsLabel}>Economies potentielles</Text>
                        <Text style={styles.demoProfileSavingsValue}>
                          {formatCurrency(profile.expected_savings)}/mois
                        </Text>
                      </View>
                      {activatingProfileKey === profile.key ? (
                        <ActivityIndicator size="small" color={colors.primary} />
                      ) : (
                        <View style={styles.demoProfileButton}>
                          <Text style={styles.demoProfileButtonText}>Tester</Text>
                          <Icon name="chevron-right" size={16} color={colors.primary} />
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Step 2: Account Selection */}
        {step === 'account' && accountDetection && (
          <View style={styles.content}>
            {/* Detected bank info */}
            <View style={styles.detectedInfoCard}>
              <View style={styles.detectedInfoHeader}>
                <Icon name="bank" size={24} color={colors.primary} />
                <Text style={styles.detectedInfoTitle}>Informations détectées</Text>
              </View>
              <View style={styles.detectedInfoGrid}>
                {accountDetection.detected.bank_name && (
                  <View style={styles.detectedInfoItem}>
                    <Text style={styles.detectedInfoLabel}>Banque</Text>
                    <Text style={styles.detectedInfoValue}>{accountDetection.detected.bank_name}</Text>
                  </View>
                )}
                {accountDetection.detected.account_number_masked && (
                  <View style={styles.detectedInfoItem}>
                    <Text style={styles.detectedInfoLabel}>Numéro de compte</Text>
                    <Text style={styles.detectedInfoValue}>****{accountDetection.detected.account_number_masked}</Text>
                  </View>
                )}
                {accountDetection.detected.iban_masked && (
                  <View style={styles.detectedInfoItem}>
                    <Text style={styles.detectedInfoLabel}>IBAN</Text>
                    <Text style={styles.detectedInfoValue}>{accountDetection.detected.iban_masked}</Text>
                  </View>
                )}
                {accountDetection.detected.account_type && (
                  <View style={styles.detectedInfoItem}>
                    <Text style={styles.detectedInfoLabel}>Type de compte</Text>
                    <Text style={styles.detectedInfoValue}>
                      {accountDetection.detected.account_type === 'checking' ? 'Compte courant' :
                       accountDetection.detected.account_type === 'savings' ? 'Épargne' :
                       accountDetection.detected.account_type === 'investment' ? 'Investissement' :
                       accountDetection.detected.account_type}
                    </Text>
                  </View>
                )}
                {accountDetection.detected.balance != null && (
                  <View style={styles.detectedInfoItem}>
                    <Text style={styles.detectedInfoLabel}>Solde</Text>
                    <Text style={styles.detectedInfoValue}>{formatCurrency(accountDetection.detected.balance)}</Text>
                  </View>
                )}
                {accountDetection.detected.period && (
                  <View style={styles.detectedInfoItem}>
                    <Text style={styles.detectedInfoLabel}>Période</Text>
                    <Text style={styles.detectedInfoValue}>
                      {new Date(accountDetection.detected.period.start).toLocaleDateString('fr-FR')} - {new Date(accountDetection.detected.period.end).toLocaleDateString('fr-FR')}
                    </Text>
                  </View>
                )}
              </View>
              {accountDetection.detected.confidence > 0 && (
                <View style={styles.confidenceBadge}>
                  <Icon
                    name={accountDetection.detected.confidence >= 0.8 ? 'check-circle' : 'alert-circle-outline'}
                    size={14}
                    color={accountDetection.detected.confidence >= 0.8 ? colors.success : colors.warning}
                  />
                  <Text style={[
                    styles.confidenceText,
                    { color: accountDetection.detected.confidence >= 0.8 ? colors.success : colors.warning }
                  ]}>
                    Confiance: {Math.round(accountDetection.detected.confidence * 100)}%
                  </Text>
                </View>
              )}
            </View>

            {/* Account matches (auto-detected) */}
            {accountDetection.matches.length > 0 && (
              <>
                <Text style={styles.sectionTitle}>Correspondances détectées</Text>
                <Text style={styles.sectionSubtitle}>
                  {accountDetection.suggestion === 'attach_existing'
                    ? 'Ce relevé correspond à un de vos comptes'
                    : 'Ces comptes semblent correspondre'}
                </Text>
                {accountDetection.matches.map((match) => (
                  <TouchableOpacity
                    key={match.account_id}
                    style={[
                      styles.accountMatchCard,
                      selectedAccountId === match.account_id && styles.accountMatchCardSelected
                    ]}
                    onPress={() => handleAccountSelect(match.account_id)}
                  >
                    <View style={styles.accountMatchHeader}>
                      <View style={styles.accountMatchInfo}>
                        <Text style={styles.accountMatchName}>{match.name}</Text>
                        {match.institution_name && (
                          <Text style={styles.accountMatchBank}>{match.institution_name}</Text>
                        )}
                      </View>
                      <View style={[
                        styles.matchTypeBadge,
                        match.match_type === 'exact_iban' && styles.matchTypeBadgeExact
                      ]}>
                        <Text style={[
                          styles.matchTypeText,
                          match.match_type === 'exact_iban' && styles.matchTypeTextExact
                        ]}>
                          {match.match_type === 'exact_iban' ? 'IBAN exact' :
                           match.match_type === 'partial_iban' ? 'IBAN partiel' :
                           match.match_type === 'bank_name' ? 'Même banque' : 'Similaire'}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.accountMatchDetails}>
                      {match.iban_masked && (
                        <Text style={styles.accountMatchDetail}>{match.iban_masked}</Text>
                      )}
                      <Text style={styles.accountMatchDetail}>
                        {match.transaction_count} transactions
                      </Text>
                    </View>
                    {selectedAccountId === match.account_id && (
                      <View style={styles.accountMatchCheck}>
                        <Icon name="check-circle" size={24} color={colors.primary} />
                      </View>
                    )}
                  </TouchableOpacity>
                ))}
              </>
            )}

            {/* Other existing accounts (not matched but available) */}
            {(() => {
              const matchedIds = accountDetection.matches.map(m => m.account_id);
              const otherAccounts = (accountDetection.existing_accounts || [])
                .filter(a => !matchedIds.includes(a.account_id));

              if (otherAccounts.length === 0) return null;

              return (
                <>
                  <Text style={styles.sectionTitle}>
                    {accountDetection.matches.length > 0 ? 'Autres comptes' : 'Sélectionner un compte existant'}
                  </Text>
                  {accountDetection.matches.length === 0 && (
                    <Text style={styles.sectionSubtitle}>
                      Aucune correspondance automatique trouvée. Sélectionnez manuellement.
                    </Text>
                  )}
                  {otherAccounts.map((account) => (
                    <TouchableOpacity
                      key={account.account_id}
                      style={[
                        styles.accountMatchCard,
                        selectedAccountId === account.account_id && styles.accountMatchCardSelected
                      ]}
                      onPress={() => handleAccountSelect(account.account_id)}
                    >
                      <View style={styles.accountMatchHeader}>
                        <View style={styles.accountMatchInfo}>
                          <Text style={styles.accountMatchName}>{account.name}</Text>
                          {account.institution_name && (
                            <Text style={styles.accountMatchBank}>{account.institution_name}</Text>
                          )}
                        </View>
                      </View>
                      <View style={styles.accountMatchDetails}>
                        {account.iban_masked && (
                          <Text style={styles.accountMatchDetail}>{account.iban_masked}</Text>
                        )}
                        <Text style={styles.accountMatchDetail}>
                          {account.transaction_count} transactions
                        </Text>
                      </View>
                      {selectedAccountId === account.account_id && (
                        <View style={styles.accountMatchCheck}>
                          <Icon name="check-circle" size={24} color={colors.primary} />
                        </View>
                      )}
                    </TouchableOpacity>
                  ))}
                </>
              );
            })()}

            {/* Create new account option */}
            <TouchableOpacity
              style={[
                styles.createNewAccountCard,
                createNewAccount && styles.createNewAccountCardSelected
              ]}
              onPress={handleCreateNewAccount}
            >
              <Icon name="plus-circle-outline" size={24} color={createNewAccount ? colors.primary : colors.textSecondary} />
              <View style={styles.createNewAccountInfo}>
                <Text style={[styles.createNewAccountTitle, createNewAccount && { color: colors.primary }]}>
                  Créer un nouveau compte
                </Text>
                <Text style={styles.createNewAccountSubtitle}>
                  {accountDetection.detected.bank_name
                    ? `Nouveau compte ${accountDetection.detected.bank_name}`
                    : 'Ajouter ce relevé à un nouveau compte'}
                </Text>
              </View>
              {createNewAccount && (
                <Icon name="check-circle" size={24} color={colors.primary} />
              )}
            </TouchableOpacity>

            {/* Continue button */}
            <View style={styles.accountActions}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={() => setStep('upload')}
              >
                <Text style={styles.cancelButtonText}>Retour</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.button,
                  styles.confirmButton,
                  (!selectedAccountId && !createNewAccount) && styles.buttonDisabled
                ]}
                onPress={proceedToPreview}
                disabled={!selectedAccountId && !createNewAccount}
              >
                <Text style={styles.confirmButtonText}>Continuer</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Step 3: Preview & Mapping */}
        {step === 'preview' && (
          <View style={styles.content}>
            {/* PDF: bank detection badge */}
            {fileFormat === 'pdf' && (
              <View style={styles.pdfParserBadge}>
                <Icon name="file-pdf-box" size={18} color={colors.primary} />
                <Text style={styles.pdfParserText}>
                  {bankDetected
                    ? `Relevé ${bankDetected} détecté`
                    : 'Banque non reconnue — parseur générique utilisé'}
                </Text>
              </View>
            )}

            {/* PDF: bank selector when generic parser was used */}
            {fileFormat === 'pdf' && !bankDetected && availableParsers.length > 0 && (
              <View style={styles.bankSelector}>
                <Text style={styles.bankSelectorTitle}>Sélectionne ta banque pour un meilleur résultat</Text>
                <View style={styles.bankSelectorOptions}>
                  {availableParsers.map((parser) => (
                    <TouchableOpacity
                      key={parser.key}
                      style={[
                        styles.bankSelectorOption,
                        selectedBankHint === parser.key && styles.bankSelectorOptionSelected,
                      ]}
                      onPress={() => setSelectedBankHint(parser.key)}
                    >
                      <Text style={[
                        styles.bankSelectorOptionText,
                        selectedBankHint === parser.key && styles.bankSelectorOptionTextSelected,
                      ]}>
                        {parser.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {selectedBankHint && (
                  <TouchableOpacity
                    style={styles.reanalyzeButton}
                    onPress={() => sendToBackend(rawCsvContent, undefined, selectedBankHint)}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <>
                        <Icon name="refresh" size={18} color="#FFFFFF" />
                        <Text style={styles.reanalyzeButtonText}>Ré-analyser</Text>
                      </>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            )}

            {/* Complete mapping: read-only summary (CSV only) */}
            {fileFormat === 'csv' && mappingComplete && !showMappingEditor && activeMappingEntries.length > 0 && renderMappingSummary()}

            {/* Incomplete mapping: prompt user to complete (CSV only) */}
            {fileFormat === 'csv' && !mappingComplete && !showMappingEditor && (
              <View style={styles.incompleteMapping}>
                <View style={styles.incompleteMappingHeader}>
                  <Icon name="alert-circle-outline" size={18} color={colors.warning} />
                  <Text style={styles.incompleteMappingTitle}>
                    {activeMappingEntries.length > 0 ? 'Détection partielle — complétez le mapping' : 'Colonnes non détectées'}
                  </Text>
                </View>
                {activeMappingEntries.length > 0 && (
                  <View style={styles.mappingChips}>
                    {activeMappingEntries.map(([field, header]) => (
                      <View key={field} style={styles.mappingChip}>
                        <Text style={styles.mappingChipLabel}>{FIELD_LABELS[field] || field}</Text>
                        <Icon name="arrow-right" size={12} color={colors.textSecondary} />
                        <Text style={styles.mappingChipValue}>{header}</Text>
                      </View>
                    ))}
                  </View>
                )}
                <Text style={styles.incompleteMappingHint}>
                  {!columnMapping?.amount && !columnMapping?.credit && !columnMapping?.debit
                    ? 'Colonne Montant (ou Crédit/Débit) manquante'
                    : !columnMapping?.date ? 'Colonne Date manquante' : 'Colonne Description manquante'}
                </Text>
                <TouchableOpacity style={styles.editMappingButton} onPress={() => setShowMappingEditor(true)}>
                  <Icon name="pencil" size={16} color={colors.primary} />
                  <Text style={styles.editMappingButtonText}>Compléter le mapping</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Editable mapping editor (CSV only) */}
            {fileFormat === 'csv' && showMappingEditor && (
              <>
                <View style={styles.mappingSummaryHeader}>
                  <Text style={styles.sectionTitle}>Correspondance des colonnes</Text>
                  {activeMappingEntries.length > 0 && (
                    <TouchableOpacity onPress={() => setShowMappingEditor(false)}>
                      <Text style={styles.mappingEditLink}>Masquer</Text>
                    </TouchableOpacity>
                  )}
                </View>

                {csvHeaders.length > 0 && (
                  <View style={styles.csvHeadersPreview}>
                    <Text style={styles.csvHeadersLabel}>Colonnes disponibles dans le CSV :</Text>
                    <View style={styles.mappingChips}>
                      {csvHeaders.filter(h => h).map((header, idx) => (
                        <View key={idx} style={styles.csvHeaderChip}>
                          <Text style={styles.csvHeaderChipText}>{header}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                <View style={styles.mappingContainer}>
                  {renderMappingField('date', 'Date', true)}
                  {renderMappingField('description', 'Description', true)}
                  {renderMappingField('amount', 'Montant', false)}
                  {renderMappingField('credit', 'Crédit', false)}
                  {renderMappingField('debit', 'Débit', false)}
                </View>
                <Text style={styles.mappingHelpText}>
                  Montant requis : remplissez soit Montant seul, soit Crédit + Débit
                </Text>
                <TouchableOpacity
                  style={[styles.reanalyzeButton, !isMappingComplete && styles.buttonDisabled]}
                  onPress={handleResubmitMapping}
                  disabled={isProcessing || !isMappingComplete}
                >
                  {isProcessing ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <>
                      <Icon name="refresh" size={18} color="#FFFFFF" />
                      <Text style={styles.reanalyzeButtonText}>Analyser</Text>
                    </>
                  )}
                </TouchableOpacity>
              </>
            )}

            {/* Stats */}
            {stats && stats.total > 0 && (
              <View style={styles.statsCard}>
                <View style={styles.statsRow}>
                  <View style={styles.statItem}>
                    <Text style={styles.statValue}>{stats.total}</Text>
                    <Text style={styles.statLabel}>transactions</Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={[styles.statValue, { color: colors.success }]}>{stats.income}</Text>
                    <Text style={styles.statLabel}>revenus</Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={[styles.statValue, { color: colors.error }]}>{stats.expense}</Text>
                    <Text style={styles.statLabel}>dépenses</Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={styles.statValue}>{stats.categorized}</Text>
                    <Text style={styles.statLabel}>catégorisées</Text>
                  </View>
                </View>
              </View>
            )}

            {/* Preview rows */}
            {previewRows.length > 0 && (
              <>
                <Text style={styles.sectionTitle}>Aperçu ({Math.min(previewRows.length, 5)} premières lignes)</Text>
                <View style={styles.previewTable}>
                  <View style={styles.previewHeaderRow}>
                    <Text style={[styles.previewHeaderCell, { flex: 2 }]}>Description</Text>
                    <Text style={[styles.previewHeaderCell, { flex: 1 }]}>Montant</Text>
                    <Text style={[styles.previewHeaderCell, { flex: 1 }]}>Date</Text>
                    <Text style={[styles.previewHeaderCell, { flex: 1 }]}>Catégorie</Text>
                  </View>
                  {previewRows.slice(0, 5).map((row, idx) => (
                    <View key={idx} style={styles.previewRow}>
                      <Text style={[styles.previewCell, { flex: 2 }]} numberOfLines={1}>{row.description}</Text>
                      <Text style={[styles.previewCell, { flex: 1 }, row.transaction_type === 'income' ? styles.incomeText : styles.expenseText]}>
                        {row.transaction_type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(row.amount))}
                      </Text>
                      <Text style={[styles.previewCell, { flex: 1 }]}>{row.transaction_date}</Text>
                      <Text style={[styles.previewCell, { flex: 1 }]}>{row.category}</Text>
                    </View>
                  ))}
                </View>
              </>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <View style={styles.errorsContainer}>
                <Text style={styles.errorsTitle}>{errors.length} erreur{errors.length > 1 ? 's' : ''} de parsing</Text>
                {errors.slice(0, 3).map((err, idx) => (
                  <Text key={idx} style={styles.errorItem}>Ligne {err.row}: {err.error}</Text>
                ))}
                {errors.length > 3 && (
                  <Text style={styles.errorItem}>...et {errors.length - 3} autre{errors.length - 3 > 1 ? 's' : ''}</Text>
                )}
                <TouchableOpacity
                  onPress={() => openWhatsAppSupport(`Import: ${errors.length} erreurs de parsing`)}
                  style={styles.errorWhatsappLink}
                >
                  <Text style={styles.errorWhatsappText}>Contacter le support</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Confirm / Cancel */}
            {previewRows.length > 0 && (
              <View style={styles.actions}>
                <TouchableOpacity style={[styles.button, styles.cancelButton]} onPress={resetState}>
                  <Text style={styles.cancelButtonText}>Annuler</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, styles.confirmButton, isProcessing && styles.buttonDisabled]}
                  onPress={handleConfirmImport}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.confirmButtonText}>Importer {stats?.total || previewRows.length} transactions</Text>
                  )}
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: spacing.xl,
  },

  // Header
  header: {
    backgroundColor: colors.backgroundSecondary,
    paddingTop: Platform.OS === 'ios' ? 60 : spacing.xl,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  closeButton: {
    alignSelf: 'flex-end',
    padding: spacing.xs,
  },
  closeButtonText: {
    fontSize: typography.fontSize.xl,
    color: colors.textSecondary,
  },
  headerTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  progressContainer: {
    marginTop: spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: colors.backgroundTertiary,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
  },
  stepLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
  },
  stepLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  stepLabelActive: {
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold as '600',
  },

  // Content
  content: {
    flex: 1,
    padding: spacing.lg,
  },

  // Upload step
  uploadArea: {
    alignItems: 'center',
    padding: spacing.xl,
    borderWidth: 2,
    borderColor: colors.border,
    borderStyle: 'dashed',
    borderRadius: 12,
    marginBottom: spacing.md,
  },
  uploadTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  uploadDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: spacing.lg,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 32,
  },
  uploadButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
  supportedFormats: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  supportedTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  supportedText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  helpBox: {
    flexDirection: 'row',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    padding: spacing.md,
  },
  helpIcon: {
    fontSize: typography.fontSize.lg,
    marginRight: spacing.sm,
  },
  helpText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  valueBox: {
    flexDirection: 'row',
    backgroundColor: colors.primary + '08',
    borderRadius: 8,
    padding: spacing.md,
    marginTop: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary + '20',
  },
  valueIcon: {
    fontSize: typography.fontSize.xl,
    marginRight: spacing.sm,
  },
  valueContent: {
    flex: 1,
  },
  valueTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  valueText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  valueBold: {
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.primary,
  },

  // Mapping summary
  mappingSummary: {
    backgroundColor: colors.success + '08',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.success + '30',
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  mappingSummaryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  mappingSummaryTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  mappingSummaryTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.success,
  },
  mappingEditLink: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  mappingChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  mappingChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.background,
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  mappingChipLabel: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.textSecondary,
  },
  mappingChipValue: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.text,
  },

  // Incomplete mapping
  incompleteMapping: {
    backgroundColor: colors.warning + '10',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.warning + '30',
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  incompleteMappingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  incompleteMappingTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.warning,
  },
  incompleteMappingHint: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  editMappingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary + '10',
    borderRadius: 8,
    paddingVertical: spacing.sm,
    marginTop: spacing.xs,
  },
  editMappingButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.primary,
  },

  // Mapping editor
  sectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.sm,
    marginTop: spacing.sm,
  },
  mappingContainer: {
    marginBottom: spacing.md,
  },
  mappingField: {
    marginBottom: spacing.sm,
  },
  mappingLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  requiredStar: {
    color: colors.error,
  },
  mappingSelect: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  mappingSelectText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  mappingPlaceholder: {
    color: colors.textSecondary,
  },
  mappingDropdown: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginTop: spacing.xs,
    maxHeight: 150,
  },
  mappingOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  mappingOptionSelected: {
    backgroundColor: colors.primary + '10',
  },
  mappingOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  mappingOptionTextSelected: {
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold as '600',
  },
  mappingHelpText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    fontStyle: 'italic',
  },
  csvHeadersPreview: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.md,
  },
  csvHeadersLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  csvHeaderChip: {
    backgroundColor: colors.background,
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
  },
  csvHeaderChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.text,
  },
  reanalyzeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  reanalyzeButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },

  // Stats
  statsCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
  },
  statLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },

  // Preview
  previewTable: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: spacing.md,
  },
  previewHeaderRow: {
    flexDirection: 'row',
    backgroundColor: colors.backgroundSecondary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  previewHeaderCell: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.textSecondary,
    paddingHorizontal: 4,
  },
  previewRow: {
    flexDirection: 'row',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  previewCell: {
    fontSize: typography.fontSize.xs,
    color: colors.text,
    paddingHorizontal: 4,
  },
  incomeText: {
    color: colors.success,
  },
  expenseText: {
    color: colors.error,
  },

  // Errors
  errorsContainer: {
    backgroundColor: colors.error + '10',
    borderRadius: 8,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  errorsTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.error,
    marginBottom: spacing.xs,
  },
  errorItem: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    lineHeight: 18,
  },
  errorWhatsappLink: {
    marginTop: spacing.sm,
  },
  errorWhatsappText: {
    fontSize: typography.fontSize.xs,
    color: '#25D366',
    fontWeight: typography.fontWeight.semibold as '600',
  },

  // Actions
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  confirmButton: {
    backgroundColor: colors.primary,
  },
  confirmButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
  buttonDisabled: {
    opacity: 0.5,
  },

  // Demo section
  demoSection: {
    marginTop: spacing.xl,
  },
  demoDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  demoDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  demoDividerText: {
    paddingHorizontal: spacing.md,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  demoTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  demoSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  demoProfileCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  demoProfileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  demoProfileEmoji: {
    fontSize: 32,
    marginRight: spacing.md,
  },
  demoProfileInfo: {
    flex: 1,
  },
  demoProfileName: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  demoProfileTagline: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  demoProfileFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  demoProfileSavings: {
    flex: 1,
  },
  demoProfileSavingsLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  demoProfileSavingsValue: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.success,
  },
  demoProfileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  demoProfileButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.primary,
    marginRight: spacing.xs,
  },

  // Account step
  detectedInfoCard: {
    backgroundColor: colors.primary + '08',
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.lg,
    borderWidth: 1,
    borderColor: colors.primary + '20',
  },
  detectedInfoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  detectedInfoTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  detectedInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  detectedInfoItem: {
    minWidth: '45%',
  },
  detectedInfoLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: 2,
  },
  detectedInfoValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.text,
  },
  confidenceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: spacing.md,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.primary + '20',
  },
  confidenceText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium as '500',
  },
  sectionSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  accountMatchCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 2,
    borderColor: colors.border,
  },
  accountMatchCardSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '05',
  },
  accountMatchHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  accountMatchInfo: {
    flex: 1,
  },
  accountMatchName: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  accountMatchBank: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  matchTypeBadge: {
    backgroundColor: colors.backgroundTertiary,
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: spacing.xs,
  },
  matchTypeBadgeExact: {
    backgroundColor: colors.success + '15',
  },
  matchTypeText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  matchTypeTextExact: {
    color: colors.success,
  },
  accountMatchDetails: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  accountMatchDetail: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  accountMatchCheck: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
  },
  createNewAccountCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginTop: spacing.md,
    borderWidth: 2,
    borderColor: colors.border,
    borderStyle: 'dashed',
    gap: spacing.md,
  },
  createNewAccountCardSelected: {
    borderColor: colors.primary,
    borderStyle: 'solid',
    backgroundColor: colors.primary + '05',
  },
  createNewAccountInfo: {
    flex: 1,
  },
  createNewAccountTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  createNewAccountSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  accountActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xl,
  },

  // PDF parser badge
  pdfParserBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary + '10',
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.md,
  },
  pdfParserText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },

  // Bank selector (PDF fallback)
  bankSelector: {
    backgroundColor: colors.warning + '10',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.warning + '30',
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  bankSelectorTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.warning,
    marginBottom: spacing.sm,
  },
  bankSelectorOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  bankSelectorOption: {
    backgroundColor: colors.background,
    borderRadius: 6,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  bankSelectorOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '10',
  },
  bankSelectorOptionText: {
    fontSize: typography.fontSize.xs,
    color: colors.text,
  },
  bankSelectorOptionTextSelected: {
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold as '600',
  },

});
